import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiDelete, apiGet, apiPatch, apiPost, apiPut, type QueryParams } from './client';
import { keys } from './keys';
import type {
  AuthState,
  AccountProfileResponse,
  AccountProfileUpdate,
  AccountSecurityOverview,
  AccountSecuritySessionRevoked,
  AccountDeletionOverview,
  AccountDeletionStatus,
  AccountDeletionScheduleRequest,
  AccountDeletionCancelRequest,
  AccountMfaSetupRequest,
  AccountMfaSetupResponse,
  AccountMfaCodeRequest,
  AccountMfaConfirmation,
  AccountMfaDisableRequest,
  AccountMfaDisabled,
  AccountPasskeySetupRequest,
  AccountPasskeyConfirmation,
  AccountPasskeyCreated,
  AccountPasskeyRevokeRequest,
  AccountPasskeyRevoked,
  PasskeyOptionsResponse,
  AccountDataExport,
  WorkspaceOverview,
  WorkspaceRename,
  WorkspaceOrganizationCreate,
  WorkspaceOrganizationCreated,
  WorkspaceMemberRoleChange,
  WorkspaceMemberImpact,
  WorkspaceMemberStateChange,
  WorkspaceMemberStateChanged,
  WorkspaceOwnershipTransfer,
  WorkspaceInvitationCreate,
  WorkspaceInvitationIssued,
  WorkspaceInvitationRevoked,
  WorkspaceSwitch,
  SearchProfileArchive,
  SearchProfileClone,
  SearchProfileCohortPreview,
  SearchProfileCohortPreviewRequest,
  SearchProfileCreate,
  SearchProfileHistory,
  SearchProfileItem,
  SearchProfileRefreshAdmission,
  SearchProfileRefreshRequest,
  SearchProfileList,
  SearchProfileStateChange,
  SearchProfileUpdate,
  SignalRuleCreate,
  SignalRuleItem,
  SignalRuleJournal,
  SignalRuleList,
  SignalRulePreview,
  SignalRulePreviewRequest,
  SignalRuleTestResult,
  SignalRuleUpdate,
  NotificationItem,
  NotificationPreferenceOut,
  NotificationPreferencesResponse,
  NotificationPreferenceUpdate,
  NotificationsResponse,
  NotificationsSummary,
  NotificationStateUpdate,
  ApiEvent,
  Combination,
  Dashboard,
  PageHistory,
  PagesResponse,
  PageWatchCreateRequest,
  PageWatchItem,
  DeparturesMatrix,
  DirectionsResponse,
  EventStatus,
  EventsResponse,
  FlightsResponse,
  Meta,
  OperatorsResponse,
  ResortAlternativesResponse,
  ResortsResponse,
  RuntimeStatus,
  StayOperatorsResponse,
  OfferKind,
  SourcesResponse,
  StayDetail,
  StayBundle,
  StayHistory,
  StaysResponse,
  PartySettings,
  PartySettingsUpdate,
  Place,
  PricePoint,
  VariantsResponse,
  WatchCreateRequest,
  WatchItem,
  WatchResponse,
  WatchUpdateRequest,
  SignalExplanation,
  TenantAuditPage,
  TenantUsageSummary,
} from './types';

export function useAccountProfile() {
  return useQuery({
    queryKey: keys.accountProfile(),
    queryFn: () => apiGet<AccountProfileResponse>('/api/account/profile'),
    staleTime: 60_000,
  });
}

export function useUpdateAccountProfile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: AccountProfileUpdate) =>
      apiPatch<AccountProfileResponse>('/api/account/profile', body),
    onSuccess: (profile) => client.setQueryData(keys.accountProfile(), profile),
  });
}

export function useAccountSecurity() {
  return useQuery({
    queryKey: keys.accountSecurity(),
    queryFn: () => apiGet<AccountSecurityOverview>('/api/account/security'),
    staleTime: 60_000,
  });
}

export function useRevokeAccountSession() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (sessionRef: string) =>
      apiDelete<AccountSecuritySessionRevoked>(`/api/account/security/sessions/${sessionRef}`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.accountSecurity() }),
  });
}

export function useSetupAccountMfa() {
  return useMutation({
    mutationFn: (body: AccountMfaSetupRequest) =>
      apiPost<AccountMfaSetupResponse>('/api/account/security/mfa/setup', body),
  });
}

export function useConfirmAccountMfa() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: AccountMfaCodeRequest) =>
      apiPost<AccountMfaConfirmation>('/api/account/security/mfa/confirm', body),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.accountSecurity() }),
  });
}

export function useDisableAccountMfa() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: AccountMfaDisableRequest) =>
      apiPost<AccountMfaDisabled>('/api/account/security/mfa/disable', body),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.accountSecurity() }),
  });
}

export function useSetupAccountPasskey() {
  return useMutation({
    mutationFn: (body: AccountPasskeySetupRequest) =>
      apiPost<PasskeyOptionsResponse>('/api/account/security/passkeys/options', body),
  });
}

export function useConfirmAccountPasskey() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: AccountPasskeyConfirmation) =>
      apiPost<AccountPasskeyCreated>('/api/account/security/passkeys/confirm', body),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.accountSecurity() }),
  });
}

export function useRevokeAccountPasskey() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ passkeyId, body }: { passkeyId: string; body: AccountPasskeyRevokeRequest }) =>
      apiPost<AccountPasskeyRevoked>(`/api/account/security/passkeys/${passkeyId}/revoke`, body),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.accountSecurity() }),
  });
}

export function useAccountDataExport() {
  return useMutation({
    mutationFn: () => apiPost<AccountDataExport>('/api/account/export', {}),
  });
}

export function useAccountDeletion() {
  return useQuery({
    queryKey: keys.accountDeletion(),
    queryFn: () => apiGet<AccountDeletionOverview>('/api/account/deletion'),
    staleTime: 30_000,
  });
}

export function useScheduleAccountDeletion() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: AccountDeletionScheduleRequest) =>
      apiPost<AccountDeletionStatus>('/api/account/deletion', body),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.accountDeletion() }),
  });
}

export function useCancelAccountDeletion() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: AccountDeletionCancelRequest) =>
      apiPost<AccountDeletionStatus>('/api/account/deletion/cancel', body),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.accountDeletion() }),
  });
}

export function useWorkspace() {
  return useQuery({
    queryKey: keys.workspace(),
    queryFn: () => apiGet<WorkspaceOverview>('/api/workspace'),
    staleTime: 30_000,
  });
}

export function useWorkspaceMemberAudit(enabled: boolean) {
  return useQuery({
    queryKey: ['tc', 'workspace', 'member-audit'],
    queryFn: () => apiGet<TenantAuditPage>('/api/workspace/member-audit', { page_size: 20 }),
    enabled,
    staleTime: 30_000,
  });
}

export function useRenameWorkspace() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: WorkspaceRename) =>
      apiPatch<WorkspaceOverview>('/api/workspace', body),
    onSuccess: (workspace) => {
      client.setQueryData(keys.workspace(), workspace);
      void client.invalidateQueries({ queryKey: keys.usage() });
    },
  });
}

export function useCreateOrganizationWorkspace() {
  return useMutation({
    mutationFn: (body: WorkspaceOrganizationCreate) =>
      apiPost<WorkspaceOrganizationCreated>('/api/workspace/organizations', body),
    onSuccess: () => window.location.assign('/workspace'),
  });
}

export function useChangeWorkspaceMemberRole() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId, body }: { membershipId: string; body: WorkspaceMemberRoleChange }) =>
      apiPatch<WorkspaceOverview>(`/api/workspace/members/${membershipId}/role`, body),
    onSuccess: (workspace) => client.setQueryData(keys.workspace(), workspace),
  });
}

export function useTransferWorkspaceOwnership() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: WorkspaceOwnershipTransfer) =>
      apiPost<WorkspaceOverview>('/api/workspace/ownership-transfer', body),
    onSuccess: (workspace) => {
      client.setQueryData(keys.workspace(), workspace);
      void client.invalidateQueries({ queryKey: keys.usage() });
    },
  });
}

export function useWorkspaceMemberImpact() {
  return useMutation({
    mutationFn: (membershipId: string) =>
      apiGet<WorkspaceMemberImpact>(`/api/workspace/members/${membershipId}/impact`),
  });
}

export function useChangeWorkspaceMemberState() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId, body }: { membershipId: string; body: WorkspaceMemberStateChange }) =>
      apiPatch<WorkspaceMemberStateChanged>(`/api/workspace/members/${membershipId}/state`, body),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.workspace() }),
  });
}

export function useInviteWorkspaceMember() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: WorkspaceInvitationCreate) =>
      apiPost<WorkspaceInvitationIssued>('/api/workspace/members/invitations', body),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.workspace() }),
  });
}

export function useResendWorkspaceInvitation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (membershipId: string) =>
      apiPost<WorkspaceInvitationIssued>(`/api/workspace/members/${membershipId}/invitation`, {}),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.workspace() }),
  });
}

export function useRevokeWorkspaceInvitation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (membershipId: string) =>
      apiDelete<WorkspaceInvitationRevoked>(`/api/workspace/members/${membershipId}/invitation`),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.workspace() }),
  });
}

export function useSwitchWorkspace() {
  return useMutation({
    mutationFn: (body: WorkspaceSwitch) => apiPost<AuthState>('/api/workspace/switch', body),
    onSuccess: () => window.location.assign('/workspace'),
  });
}

export function useSearchProfiles(includeArchived = false) {
  return useQuery({
    queryKey: keys.searchProfiles(includeArchived),
    queryFn: () => apiGet<SearchProfileList>('/api/search-profiles', {
      include_archived: includeArchived,
    }),
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
  });
}

export function useSearchProfileHistory(profileId: string | null) {
  return useQuery({
    queryKey: keys.searchProfileHistory(profileId ?? ''),
    queryFn: () => apiGet<SearchProfileHistory>(`/api/search-profiles/${profileId}/history`),
    enabled: profileId !== null,
  });
}

function useRefreshSearchProfiles() {
  const client = useQueryClient();
  return (profile: SearchProfileItem) => {
    void client.invalidateQueries({ queryKey: ['tc', 'search-profiles'] });
    void client.invalidateQueries({ queryKey: keys.searchProfileHistory(profile.profile_id) });
  };
}

export function useCreateSearchProfile() {
  const refresh = useRefreshSearchProfiles();
  return useMutation({
    mutationFn: (body: SearchProfileCreate) => apiPost<SearchProfileItem>('/api/search-profiles', body),
    onSuccess: refresh,
  });
}

export function usePreviewSearchProfile() {
  return useMutation({
    mutationFn: (body: SearchProfileCohortPreviewRequest) =>
      apiPost<SearchProfileCohortPreview>('/api/search-profiles/preview', body),
  });
}

export function useRefreshSearchProfileNow() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ profileId, body }: { profileId: string; body: SearchProfileRefreshRequest }) =>
      apiPost<SearchProfileRefreshAdmission>(`/api/search-profiles/${profileId}/refresh`, body),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['tc', 'search-profiles'] }),
  });
}

export function useUpdateSearchProfile() {
  const refresh = useRefreshSearchProfiles();
  return useMutation({
    mutationFn: ({ profileId, body }: { profileId: string; body: SearchProfileUpdate }) =>
      apiPatch<SearchProfileItem>(`/api/search-profiles/${profileId}`, body),
    onSuccess: refresh,
  });
}

export function useCloneSearchProfile() {
  const refresh = useRefreshSearchProfiles();
  return useMutation({
    mutationFn: ({ profileId, body }: { profileId: string; body: SearchProfileClone }) =>
      apiPost<SearchProfileItem>(`/api/search-profiles/${profileId}/clone`, body),
    onSuccess: refresh,
  });
}

export function useArchiveSearchProfile() {
  const refresh = useRefreshSearchProfiles();
  return useMutation({
    mutationFn: ({ profileId, body }: { profileId: string; body: SearchProfileArchive }) =>
      apiPost<SearchProfileItem>(`/api/search-profiles/${profileId}/archive`, body),
    onSuccess: refresh,
  });
}

export function usePauseSearchProfile() {
  const refresh = useRefreshSearchProfiles();
  return useMutation({
    mutationFn: ({ profileId, body }: { profileId: string; body: SearchProfileStateChange }) =>
      apiPost<SearchProfileItem>(`/api/search-profiles/${profileId}/pause`, body),
    onSuccess: refresh,
  });
}

export function useResumeSearchProfile() {
  const refresh = useRefreshSearchProfiles();
  return useMutation({
    mutationFn: ({ profileId, body }: { profileId: string; body: SearchProfileStateChange }) =>
      apiPost<SearchProfileItem>(`/api/search-profiles/${profileId}/resume`, body),
    onSuccess: refresh,
  });
}

export function useSignalRules(includeArchived = false) {
  return useQuery({
    queryKey: keys.signalRules(includeArchived),
    queryFn: () => apiGet<SignalRuleList>('/api/signal-rules', {
      include_archived: includeArchived,
    }),
  });
}

export function useSignalRuleJournal(ruleId: string | null) {
  return useQuery({
    queryKey: keys.signalRuleJournal(ruleId ?? ''),
    queryFn: () => apiGet<SignalRuleJournal>(`/api/signal-rules/${ruleId}/journal`),
    enabled: ruleId !== null,
  });
}

function useRefreshSignalRules() {
  const client = useQueryClient();
  return (rule: SignalRuleItem) => {
    void client.invalidateQueries({ queryKey: ['tc', 'signal-rules'] });
    void client.invalidateQueries({ queryKey: keys.signalRuleJournal(rule.rule_id) });
  };
}

export function useCreateSignalRule() {
  const refresh = useRefreshSignalRules();
  return useMutation({
    mutationFn: (body: SignalRuleCreate) => apiPost<SignalRuleItem>('/api/signal-rules', body),
    onSuccess: refresh,
  });
}

export function useUpdateSignalRule() {
  const refresh = useRefreshSignalRules();
  return useMutation({
    mutationFn: ({ ruleId, body }: { ruleId: string; body: SignalRuleUpdate }) =>
      apiPatch<SignalRuleItem>(`/api/signal-rules/${ruleId}`, body),
    onSuccess: refresh,
  });
}

export function usePreviewSignalRule() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ ruleId, body }: { ruleId: string; body: SignalRulePreviewRequest }) =>
      apiPost<SignalRulePreview>(`/api/signal-rules/${ruleId}/preview`, body),
    onSuccess: (_, input) =>
      client.invalidateQueries({ queryKey: keys.signalRuleJournal(input.ruleId) }),
  });
}

export function useTestSignalRule() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) =>
      apiPost<SignalRuleTestResult>(`/api/signal-rules/${ruleId}/test`, {}),
    onSuccess: (_, ruleId) =>
      client.invalidateQueries({ queryKey: keys.signalRuleJournal(ruleId) }),
  });
}

export interface NotificationsParams {
  state?: 'unread' | 'read' | 'snoozed' | 'resolved';
  rule_id?: string;
  page?: number;
  page_size?: number;
}

function useRefreshNotifications() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: ['tc', 'notifications'] });
    void client.invalidateQueries({ queryKey: keys.notificationsSummary() });
    void client.invalidateQueries({ queryKey: keys.notificationPreferences() });
  };
}

export function useNotifications(input: NotificationsParams = {}) {
  const query = params({ ...input, page: input.page ?? 1, page_size: input.page_size ?? 20 });
  return useQuery({
    queryKey: keys.notifications(query),
    queryFn: () => apiGet<NotificationsResponse>('/api/notifications', query),
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
    placeholderData: (previous) => previous,
  });
}

export function useNotificationsSummary() {
  return useQuery({
    queryKey: keys.notificationsSummary(),
    queryFn: () => apiGet<NotificationsSummary>('/api/notifications/summary'),
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
  });
}

export function useUpdateNotification() {
  const refresh = useRefreshNotifications();
  return useMutation({
    mutationFn: ({ notificationId, body }: { notificationId: string; body: NotificationStateUpdate }) =>
      apiPatch<NotificationItem>(`/api/notifications/${notificationId}`, body),
    onSuccess: refresh,
  });
}

export function useReadAllNotifications() {
  const refresh = useRefreshNotifications();
  return useMutation({
    mutationFn: () => apiPost<NotificationsSummary>('/api/notifications/read-all', {}),
    onSuccess: refresh,
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: keys.notificationPreferences(),
    queryFn: () => apiGet<NotificationPreferencesResponse>('/api/notifications/preferences'),
  });
}

export function usePutNotificationPreference() {
  const refresh = useRefreshNotifications();
  return useMutation({
    mutationFn: ({ channel, ruleId, body }: { channel: NotificationPreferenceOut['channel']; ruleId?: string; body: NotificationPreferenceUpdate }) =>
      apiPut<NotificationPreferenceOut>(`/api/notifications/preferences/${channel}${ruleId ? `?rule_id=${encodeURIComponent(ruleId)}` : ''}`, body),
    onSuccess: refresh,
  });
}

export function useUnsubscribeNotificationChannel() {
  const refresh = useRefreshNotifications();
  return useMutation({
    mutationFn: ({ channel, ruleId }: { channel: NotificationPreferenceOut['channel']; ruleId?: string }) =>
      apiDelete<NotificationPreferenceOut>(`/api/notifications/preferences/${channel}${ruleId ? `?rule_id=${encodeURIComponent(ruleId)}` : ''}`),
    onSuccess: refresh,
  });
}

export function useRetryNotificationDelivery() {
  const refresh = useRefreshNotifications();
  return useMutation({
    mutationFn: ({ notificationId, channel }: { notificationId: string; channel: NotificationPreferenceOut['channel'] }) =>
      apiPost<NotificationItem>(`/api/notifications/${notificationId}/deliveries/${channel}/retry`, {}),
    onSuccess: refresh,
  });
}

// §3.4: перечисление сортировок ровно такое, значение по умолчанию существует.
export const STAY_SORTS = ['price', '-price', 'drop', '-drop', 'updated', '-updated'] as const;
export type StaySort = (typeof STAY_SORTS)[number];
export const DEFAULT_STAY_SORT: StaySort = 'price';

export interface DirectionsParams {
  kind?: OfferKind;
  days?: number;
  origin?: string;
  date_from?: string;
  date_to?: string;
  nights_min?: number;
  nights_max?: number;
  adults?: number;
  children_count?: number;
  children_age?: number[];
  page?: number;
  page_size?: number;
}

export interface StaysParams {
  kind?: OfferKind;
  destination?: string;
  // T49: страна и курорт внутри неё. `destination` (код города прилёта) остался
  // для переходов со станции, ленты и матрицы вылетов (Д-Т49-2).
  country?: string;
  resort?: string;
  origin?: string;
  date_from?: string;
  date_to?: string;
  nights_min?: number;
  nights_max?: number;
  price_max?: number;
  stars?: number;
  min_rating?: number;
  board?: string;
  // T29: фильтр по составу — истории разных составов не смешиваются
  adults?: number;
  children_count?: number;
  children_age?: number[];
  only_breakdown?: boolean;
  only_discounted?: boolean;
  min_observations?: number;
  sort?: StaySort;
  page?: number;
  page_size?: number;
}

export interface FlightsParams {
  origin?: string;
  destination?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}

export interface MatrixParams {
  kind?: OfferKind;
  days?: number;
  origin?: string;
  date_from?: string;
  date_to?: string;
  nights_min?: number;
  nights_max?: number;
  adults?: number;
  children_count?: number;
  children_age?: number[];
}

export interface DepartureQuotesParams extends Omit<MatrixParams, 'days'> {
  dates: string[];
  destination?: string;
  country?: string;
  resort?: string;
}

export interface DepartureQuoteItem {
  depart_date: string;
  stay_key: string;
  destination: Place;
  origin: Place | null;
  price: PricePoint;
  drop_ratio: number | null;
  stays: number;
}

export interface DepartureQuotesResponse {
  kind: OfferKind;
  dates: string[];
  items: DepartureQuoteItem[];
}

export interface EventsParams {
  event_id?: number;
  kind?: string;
  product_kind?: OfferKind;
  status?: EventStatus;
  since?: string;
  page?: number;
  page_size?: number;
}

export interface FacetCount {
  value: string;
  count: number;
}

export interface StaysFacets {
  total: number;
  min_price: number | null;
  max_price: number | null;
  stars: FacetCount[];
  ratings: FacetCount[];
  boards: FacetCount[];
  countries: FacetCount[];
  resorts: FacetCount[];
  origins: FacetCount[];
}

export interface EventsSummary {
  total: number;
  since: string | null;
  until: string;
  product_kind: OfferKind | null;
  by_status: Record<string, number>;
  by_event_kind: Record<string, number>;
  by_product_kind: Record<string, number>;
}

function params(value: Record<string, unknown>): QueryParams {
  const result: QueryParams = {};
  for (const [key, raw] of Object.entries(value)) {
    if (raw === undefined || raw === null || raw === '') continue;
    if (Array.isArray(raw) && raw.every((item) =>
      typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean'
    )) {
      result[key] = raw;
      continue;
    }
    if (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') {
      result[key] = raw;
    }
  }
  return result;
}

export interface DashboardParams {
  days?: number;
  include_pulse?: boolean;
  include_sources?: boolean;
}

export function useDashboard(input: DashboardParams = {}) {
  const query = params({
    days: input.days,
    include_pulse: input.include_pulse,
    include_sources: input.include_sources,
  });
  return useQuery({
    queryKey: keys.dashboard(query),
    queryFn: () => apiGet<Dashboard>('/api/dashboard', query),
    refetchInterval: 60_000,
    // The quality screen is often left open beside the operator workspace.
    // Keep source freshness current even while the browser panel is unfocused.
    refetchIntervalInBackground: true,
  });
}

export function useTenantUsage(profileId: string | null = null) {
  const query = params({ profile_id: profileId });
  return useQuery({
    queryKey: keys.usage(query),
    queryFn: () => apiGet<TenantUsageSummary>('/api/usage', query),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export interface AuditParams {
  method?: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  outcome?: 'succeeded' | 'rejected' | 'failed';
  page?: number;
  page_size?: number;
}

export function useAudit(input: AuditParams = {}, enabled = true) {
  const query = params({
    method: input.method,
    outcome: input.outcome,
    page: input.page ?? 1,
    page_size: input.page_size ?? 20,
  });
  return useQuery({
    queryKey: keys.audit(query),
    queryFn: () => apiGet<TenantAuditPage>('/api/audit', query),
    enabled,
  });
}

export function useDirections(input: DirectionsParams = {}, enabled = true) {
  // §3.3: kind по умолчанию tour, days 1..90 (default 14), page_size 12
  const query = params({
    kind: input.kind ?? 'tour',
    days: input.days ?? 14,
    origin: input.origin,
    date_from: input.date_from,
    date_to: input.date_to,
    nights_min: input.nights_min,
    nights_max: input.nights_max,
    adults: input.adults,
    children_count: input.children_count,
    children_age: input.children_age,
    page: input.page ?? 1,
    page_size: input.page_size ?? 12,
  });
  return useQuery({
    queryKey: keys.directions(query),
    queryFn: () => apiGet<DirectionsResponse>('/api/directions', query),
    enabled,
  });
}

export function useStays(input: StaysParams = {}, enabled = true) {
  const query = params({
    ...input,
    kind: input.kind ?? 'tour',
    sort: input.sort ?? DEFAULT_STAY_SORT,
    page: input.page ?? 1,
    page_size: Math.min(input.page_size ?? 25, 200), // §3.4: page_size ≤ 200
  });
  return useQuery({
    queryKey: keys.stays(query),
    queryFn: () => apiGet<StaysResponse>('/api/stays', query),
    enabled,
    placeholderData: (previous) => previous, // пагинация без мигания пустой таблицей
  });
}

/** Full-result facets for the exact catalogue context, independent of page. */
export function useStaysFacets(input: StaysParams = {}, enabled = true) {
  const query = params({
    ...input,
    kind: input.kind ?? 'tour',
    sort: undefined,
    page: undefined,
    page_size: undefined,
  });
  return useQuery({
    queryKey: keys.staysFacets(query),
    queryFn: () => apiGet<StaysFacets>('/api/stays/facets', query),
    enabled,
  });
}

export function useStay(stayKey: string) {
  return useQuery({
    queryKey: keys.stay(stayKey),
    queryFn: () => apiGet<StayDetail>(`/api/stays/${encodeURIComponent(stayKey)}`),
    enabled: stayKey.length > 0,
  });
}

/**
 * P3: вся внутренняя карточка относится к одному серверному снимку. Журнал
 * фактических переходов остаётся отдельным пользовательским read-model.
 */
export function useStayBundle(
  stayKey: string,
  watchId: number | null = null,
  historyDays = 90,
) {
  return useQuery({
    queryKey: keys.stayBundle(stayKey, historyDays, watchId),
    queryFn: () => apiGet<StayBundle>(
      `/api/stays/${encodeURIComponent(stayKey)}/bundle`,
      { history_days: historyDays, watch_id: watchId ?? undefined },
    ),
    enabled: stayKey.length > 0,
  });
}

export function useVariants(stayKey: string) {
  return useQuery({
    queryKey: keys.variants(stayKey),
    queryFn: () => apiGet<VariantsResponse>(`/api/stays/${encodeURIComponent(stayKey)}/variants`),
    enabled: stayKey.length > 0,
  });
}

/**
 * Д-Ф5: ОДИН запрос на всю карточку.
 * Здесь нет и не может быть цикла по variant_key — сервер отдаёт все серии,
 * best_available и leader_changes сразу.
 */
export function useStayHistory(stayKey: string, days = 90) {
  return useQuery({
    queryKey: keys.stayHistory(stayKey, days),
    queryFn: () =>
      apiGet<StayHistory>(`/api/stays/${encodeURIComponent(stayKey)}/history`, { days }),
    enabled: stayKey.length > 0,
  });
}

/**
 * T23: разложение цены своего пакета. Запрашивается только для поездок вида
 * `synth_package` — у тура и отеля разложения нет и 404 был бы ожидаемым, но
 * бессмысленным запросом на каждое открытие карточки.
 */
export function useCombination(stayKey: string, enabled: boolean) {
  return useQuery({
    queryKey: keys.combination(stayKey),
    queryFn: () => apiGet<Combination>(`/api/stays/${encodeURIComponent(stayKey)}/combination`),
    enabled: enabled && stayKey.length > 0,
  });
}

/**
 * T26: рейтинг операторов в выбранном срезе. Срез — часть ключа кэша: «Кемер»
 * и «вся Турция» отвечают на разные вопросы и путать их нельзя.
 */
export function useOperators(params: { scope?: string; scope_key?: string } = {}) {
  return useQuery({
    queryKey: keys.operators(params),
    queryFn: () => apiGet<OperatorsResponse>('/api/operators', params),
  });
}

/**
 * T26: панель «у кого дешевле» на карточке поездки. Уровень приходит в
 * ответе — фронт его не выбирает и не додумывает (Д-Т26-12).
 */
export function useStayOperators(stayKey: string) {
  return useQuery({
    queryKey: keys.stayOperators(stayKey),
    queryFn: () =>
      apiGet<StayOperatorsResponse>(`/api/stays/${encodeURIComponent(stayKey)}/operators`),
    enabled: stayKey.length > 0,
  });
}

/**
 * T26.7: чем заменить курорт. Спрашиваем по поездке: канонический ключ курорта
 * знает сервер, повторять правило канонизации на фронте нельзя — разойдётся.
 */
export function useResortAlternatives(stayKey: string) {
  return useQuery({
    queryKey: keys.resortAlternatives(stayKey),
    queryFn: () =>
      apiGet<ResortAlternativesResponse>(
        `/api/stays/${encodeURIComponent(stayKey)}/resort-alternatives`,
      ),
    enabled: stayKey.length > 0,
  });
}

/**
 * T25: страницы под наблюдением. Ошибку 503 («монитор не подключён») не
 * прячем — экран обязан объяснить, почему список пуст.
 */
/**
 * T16.4: «почему такой вывод». POST, потому что запрос может обратиться к
 * модели и всегда пишется в аудит — то есть имеет побочный эффект.
 */
export function useExplain(stayKey: string, watchId: number | null) {
  return useMutation({
    mutationFn: () =>
      apiPost<SignalExplanation>(
        `/api/stays/${encodeURIComponent(stayKey)}/explain${
          watchId === null ? '' : `?watch_id=${watchId}`
        }`,
        {},
      ),
  });
}

export function usePages() {
  return useQuery({
    queryKey: keys.pages(),
    queryFn: () => apiGet<PagesResponse>('/api/pages'),
    retry: false,
  });
}

export function usePageHistory(pageId: number | null) {
  return useQuery({
    queryKey: keys.pageHistory(pageId ?? 0),
    queryFn: () => apiGet<PageHistory>(`/api/pages/${pageId}/history`),
    enabled: pageId !== null,
  });
}

export function useCreatePage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PageWatchCreateRequest) => apiPost<PageWatchItem>('/api/pages', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.pages() }),
  });
}

export function useDeletePage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pageId: number) => apiDelete<void>(`/api/pages/${pageId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.pages() }),
  });
}

export function useFlights(input: FlightsParams = {}) {
  const query = params({ ...input, page: input.page ?? 1, page_size: input.page_size ?? 25 });
  return useQuery({
    queryKey: keys.flights(query),
    queryFn: () => apiGet<FlightsResponse>('/api/flights', query),
    placeholderData: (previous) => previous,
  });
}

export function useDeparturesMatrix(input: MatrixParams = {}) {
  const query = params({ ...input, kind: input.kind ?? 'tour', days: input.days ?? 30 });
  return useQuery({
    queryKey: keys.matrix(query),
    queryFn: () => apiGet<DeparturesMatrix>('/api/departures/matrix', query),
  });
}

/** One batched request replaces one /stays request per calendar date. */
export function useDepartureQuotes(input: DepartureQuotesParams, enabled = true) {
  const query = params({
    ...input,
    kind: input.kind ?? 'tour',
    date: input.dates,
    dates: undefined,
  });
  return useQuery({
    queryKey: keys.departureQuotes(query),
    queryFn: () => apiGet<DepartureQuotesResponse>('/api/departures/quotes', query),
    enabled: enabled && input.dates.length > 0,
  });
}

export function useEvents(
  input: EventsParams = {},
  enabled = true,
  refetchInterval: number | false = 60_000,
) {
  const query = params({ ...input, page: input.page ?? 1, page_size: input.page_size ?? 50 });
  return useQuery({
    queryKey: keys.events(query),
    queryFn: () => apiGet<EventsResponse>('/api/events', query),
    enabled,
    refetchInterval,
    placeholderData: (previous) => previous,
  });
}

/** Complete counts for the selected event window, independent of pagination. */
export function useEventsSummary(input: Omit<EventsParams, 'page' | 'page_size'> = {}) {
  const query = params(input);
  return useQuery({
    queryKey: keys.eventsSummary(query),
    queryFn: () => apiGet<EventsSummary>('/api/events/summary', query),
    refetchInterval: 60_000,
  });
}

export function useSources(input: { include_stats?: boolean } = {}) {
  const query = params({ include_stats: input.include_stats });
  return useQuery({
    queryKey: keys.sources(query),
    queryFn: () => apiGet<SourcesResponse>('/api/sources', query),
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
  });
}

export function useStatus() {
  return useQuery({
    queryKey: keys.status(),
    queryFn: () => apiGet<RuntimeStatus>('/api/status'),
    refetchInterval: 60_000,
  });
}

export function useMeta() {
  return useQuery({
    queryKey: keys.meta(),
    queryFn: () => apiGet<Meta>('/api/meta'),
    staleTime: 10 * 60_000, // словари меняются редко
  });
}

/**
 * T31: курорты выбранной страны. Запрос идёт только когда страна выбрана —
 * иначе форма дёргала бы сервер при каждом открытии.
 */
export function useResorts(country: string | undefined) {
  return useQuery({
    queryKey: keys.resorts(country ?? ''),
    queryFn: () => apiGet<ResortsResponse>('/api/meta/resorts', { country: country ?? '' }),
    enabled: Boolean(country),
    staleTime: 10 * 60_000, // справочник пакетный, меняется с релизом
  });
}

export function useWatch() {
  return useQuery({
    queryKey: keys.watch(),
    queryFn: () => apiGet<WatchResponse>('/api/watch'),
  });
}

export function useSetEventStatus() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: EventStatus }) =>
      apiPost<ApiEvent>(`/api/events/${id}/status`, { status }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['tc', 'events'] });
      void client.invalidateQueries({ queryKey: ['tc', 'events-summary'] });
      void client.invalidateQueries({ queryKey: keys.dashboard() });
    },
  });
}

/** T29: состав по умолчанию (настройки владельца). */
export function usePartySettings() {
  return useQuery({
    queryKey: keys.partySettings(),
    queryFn: () => apiGet<PartySettings>('/api/settings/party'),
  });
}

/** T29: смена состава; с apply_to_watches инвалидируется и список наблюдений. */
export function useUpdatePartySettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: PartySettingsUpdate) =>
      apiPut<PartySettings>('/api/settings/party', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.partySettings() });
      void client.invalidateQueries({ queryKey: keys.watch() });
    },
  });
}

/** T28: создание наблюдения. Сброс watch-списка и сводки. */
export function useCreateWatch() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: WatchCreateRequest) => apiPost<WatchItem>('/api/watch', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.watch() });
      void client.invalidateQueries({ queryKey: keys.dashboard() });
    },
  });
}

/** T28: правка наблюдения (страна не меняется — контракт). */
export function useUpdateWatch() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: WatchUpdateRequest }) =>
      apiPatch<WatchItem>(`/api/watch/${id}`, body),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.watch() }),
  });
}

/** T28: снятие с наблюдения. Ответ — снимок удалённого. */
export function useDeleteWatch() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiDelete<WatchItem>(`/api/watch/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.watch() });
      void client.invalidateQueries({ queryKey: keys.dashboard() });
    },
  });
}

/** §3.13: пауза и возобновление — ДВА разных маршрута без тела. */
export function usePauseWatch() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiPost<WatchItem>(`/api/watch/${id}/pause`),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.watch() }),
  });
}

export function useResumeWatch() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiPost<WatchItem>(`/api/watch/${id}/resume`),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.watch() }),
  });
}
